"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "wine-refrigerator-calculator",
  "wine-cooler-energy-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Dual Zone Wine Cooler Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Plan a two-temperature wine cooler around your collection mix. Calculates total bottle capacity and recommends how to split reds, whites, and sparkling between the two zones.",
  faqs: [
    {
      question: "What temperatures should each zone be set to?",
      answer:
        "Reds store best around 55–65°F. Whites and rosés sit around 45–55°F. Sparkling and Champagne live coldest at roughly 40–45°F. These ranges align with sommelier serving guidance from the Court of Master Sommeliers.",
    },
    {
      question: "How do I decide which zone gets the bigger compartment?",
      answer:
        "Allocate the larger compartment to whichever wine type dominates your collection. If you store mostly reds, set the larger zone warmer (55–65°F); if you serve mostly whites and bubbles, set the larger zone colder.",
    },
    {
      question: "Why should bottles be stored horizontally?",
      answer:
        "Cork-sealed bottles need contact with the wine to keep the cork moist and airtight. A dry cork shrinks, lets oxygen in, and ruins the wine — that's why dual-zone units ship with horizontal racks.",
    },
    {
      question: "Does humidity matter inside a wine cooler?",
      answer:
        "Yes. Aim for 50–70% relative humidity. Too low dries out corks; too high invites mold on labels. Most quality dual-zone units passively maintain this range.",
    },
    {
      question: "How long can sparkling wine sit in a wine cooler?",
      answer:
        "Non-vintage Champagne and most sparkling wines hold 3–4 years at proper cellar temperatures. Vintage Champagne can age 10+ years in a stable, cold zone with horizontal storage.",
    },
  ],
  relatedTools: ["wine-refrigerator-calculator", "wine-cooler-energy-calculator", "energy-cost-calculator"],
}

interface ZoneRecommendation {
  zone: string
  temperature: string
  wineTypes: string[]
  bottles: number
}

export function DualZoneWineCoolerPageClient() {
  const [redBottles, setRedBottles] = useState("12")
  const [whiteBottles, setWhiteBottles] = useState("8")
  const [sparklingBottles, setSparklingBottles] = useState("4")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const red = parseInt(redBottles) || 0
    const white = parseInt(whiteBottles) || 0
    const sparkling = parseInt(sparklingBottles) || 0

    const totalBottles = red + white + sparkling
    if (totalBottles <= 0) return null

    let recommendedSize = ""
    if (totalBottles <= 12) {
      recommendedSize = "Small Dual-Zone (12-18 bottles)"
    } else if (totalBottles <= 24) {
      recommendedSize = "Medium Dual-Zone (24-32 bottles)"
    } else if (totalBottles <= 48) {
      recommendedSize = "Large Dual-Zone (48-64 bottles)"
    } else {
      recommendedSize = "Extra Large Dual-Zone (64+ bottles)"
    }

    const zoneSetup: ZoneRecommendation[] = []
    if (red > white + sparkling) {
      zoneSetup.push({
        zone: "Larger zone (warmer)",
        temperature: "55-65°F",
        wineTypes: ["Reds", "Cabernet", "Merlot", "Syrah"],
        bottles: red,
      })
      zoneSetup.push({
        zone: "Smaller zone (colder)",
        temperature: "45-55°F",
        wineTypes: ["Whites", "Sparkling", "Champagne", "Rosé"],
        bottles: white + sparkling,
      })
    } else {
      zoneSetup.push({
        zone: "Smaller zone (warmer)",
        temperature: "55-65°F",
        wineTypes: ["Reds"],
        bottles: red,
      })
      zoneSetup.push({
        zone: "Larger zone (colder)",
        temperature: "45-55°F",
        wineTypes: ["Whites", "Sparkling", "Champagne", "Rosé"],
        bottles: white + sparkling,
      })
    }

    return { totalBottles, recommendedSize, zoneSetup }
  }, [redBottles, whiteBottles, sparklingBottles])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="One cooler,"
      heroTitleItalic="two"
      heroTitlePost="serving temperatures."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Sommelier-style zone planning"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "55–65°F", label: "Red zone target", note: "Storage" },
            { value: "45–55°F", label: "White zone target", note: "Serving" },
            { value: "40–45°F", label: "Sparkling target", note: "Coldest" },
            { value: "50–70%", label: "Ideal humidity", note: "Cork care" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "dual-zone-wine-cooler-calculator")}
      cta={{
        title: "Two zones. Right glass door. Built to age.",
        body: "Browse single- and dual-zone wine coolers from compact 6-bottle units to 150-bottle built-ins.",
        buttonLabel: "Browse wine coolers",
        buttonHref: "/collections/wine-fridges",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Bottle counts.
                <br />
                Zone allocation.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We allocate the bigger compartment to whichever wine type you store the most of.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Red bottles
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={redBottles}
                      onChange={(e) => setRedBottles(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">bottles</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    White bottles
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={whiteBottles}
                      onChange={(e) => setWhiteBottles(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">bottles</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Sparkling bottles
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={sparklingBottles}
                      onChange={(e) => setSparklingBottles(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">bottles</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Sparkling and Champagne should sit in the coldest zone.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Total capacity needed
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.totalBottles ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">bottles</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendedSize ?? "Enter your bottle counts."}
                  </div>
                  {result && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                      {result.zoneSetup.map((z, i) => (
                        <div key={i} className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                            {z.zone}
                          </div>
                          <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{z.temperature}</div>
                          <div className="text-[11px] text-white/70 mt-1">{z.bottles} bottles</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop dual-zone units"
          title="Wine coolers built for two temperatures"
          description="Independent thermostats, UV-protected glass, vibration-dampened compressors."
          collectionType="wine-cooler"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Two zones, <em className="italic text-[#ff9951]">honest</em> ranges.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Temperature ranges align with sommelier serving and storage guidance. Capacity tiers come from common dual-zone form factors at Fridge.com.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Total bottle count",
                  d: "Reds + whites + sparkling. The total drives form factor (compact, mid, large, XL).",
                },
                {
                  n: "02",
                  t: "Zone allocation",
                  d: "Whichever wine type is larger gets the bigger compartment. Reds run warmer (55–65°F); whites and sparkling run colder (40–55°F).",
                },
                {
                  n: "03",
                  t: "Form-factor recommendation",
                  d: "≤12 → small. 13–24 → mid. 25–48 → large built-in. 48+ → tower or full cellar.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges to compare"
          description="If you keep beer, mixers, and wine together, a beverage center is the more flexible call than a dedicated wine fridge."
          collectionType="counter-depth"
          fallbackCollectionType="wine-cooler"
        />
      </section>
    </ToolPageShell>
  )
}
